import {
  MASS_KINDS,
  MASS_SPECS,
  PLACE_GAP,
  RECEIVER_CATCH,
  RECEIVER_RADIUS,
  SOURCE_RADIUS,
  WALL_HALF,
  WORLD_H,
  WORLD_W,
  isValidColor,
} from './constants';
import {
  type BeamTrace,
  type Scene,
  type SceneCircle,
  type TraceOptions,
  traceBeam,
} from './physics';
import type {
  Disc,
  Inventory,
  Level,
  Mass,
  MassKind,
  Nebula,
  Receiver,
  Source,
  Wall,
} from './types';

const DEG = Math.PI / 180;

/** All masses in play: fixed ones first, then the placed ones. */
export function allMasses(level: Level, placed: readonly Mass[]): Mass[] {
  return [...level.fixed, ...placed];
}

export function buildScene(level: Level, placed: readonly Mass[]): Scene {
  const masses = allMasses(level, placed);
  const packed = new Float64Array(masses.length * 3);
  const circles: SceneCircle[] = [];
  masses.forEach((m, i) => {
    const spec = MASS_SPECS[m.kind];
    packed[i * 3] = m.x;
    packed[i * 3 + 1] = m.y;
    packed[i * 3 + 2] = spec.mu;
    circles.push({
      x: m.x,
      y: m.y,
      r: spec.radius,
      role: m.kind === 'hole' ? 'captured' : 'blocked',
      ref: i,
    });
  });
  level.receivers.forEach((r, i) =>
    circles.push({ x: r.x, y: r.y, r: RECEIVER_CATCH, role: 'receiver', ref: i }),
  );
  for (const s of level.sources) {
    circles.push({ x: s.x, y: s.y, r: SOURCE_RADIUS, role: 'blocked', ref: -1 });
  }
  for (const r of level.rocks) circles.push({ x: r.x, y: r.y, r: r.r, role: 'blocked', ref: -1 });
  for (const d of level.dust) circles.push({ x: d.x, y: d.y, r: d.r, role: 'absorbed', ref: -1 });
  return {
    masses: packed,
    massCount: masses.length,
    circles,
    walls: level.walls.map((w) => ({ ...w, half: WALL_HALF })),
    nebulae: level.nebulae.map((n) => ({ ...n })),
    x0: 0,
    y0: 0,
    x1: WORLD_W,
    y1: WORLD_H,
  };
}

export interface BeamStart {
  source: number;
  x: number;
  y: number;
  /** Direction in radians. */
  angle: number;
  color: number;
}

/** The individual beams a level emits (fans expand into several beams). */
export function beamStarts(level: Level): BeamStart[] {
  const out: BeamStart[] = [];
  level.sources.forEach((s, si) => {
    const n = Math.max(1, Math.min(3, s.beams ?? 1));
    const spread = s.spread ?? 0;
    for (let k = 0; k < n; k++) {
      const a = (s.angle + (k - (n - 1) / 2) * spread) * DEG;
      const off = SOURCE_RADIUS + 0.5;
      out.push({
        source: si,
        x: s.x + Math.cos(a) * off,
        y: s.y + Math.sin(a) * off,
        angle: a,
        color: s.color,
      });
    }
  });
  return out;
}

export interface ReceiverState {
  hits: number;
  /** Union of the colours arriving. */
  mask: number;
  lit: boolean;
}

export interface LevelTrace {
  beams: (BeamTrace & { source: number })[];
  receivers: ReceiverState[];
  won: boolean;
  /** Number of beams swallowed by black holes. */
  captured: number;
}

export function receiverNeeds(r: Receiver): number {
  return Math.max(1, Math.min(3, r.count ?? 1));
}

export function traceLevel(
  level: Level,
  placed: readonly Mass[],
  options?: TraceOptions,
): LevelTrace {
  const scene = buildScene(level, placed);
  const beams = beamStarts(level).map((b) => ({
    ...traceBeam(scene, b.x, b.y, Math.cos(b.angle), Math.sin(b.angle), b.color, options),
    source: b.source,
  }));
  const receivers: ReceiverState[] = level.receivers.map(() => ({ hits: 0, mask: 0, lit: false }));
  let captured = 0;
  for (const b of beams) {
    if (b.reason === 'receiver' && b.color !== 0) {
      const st = receivers[b.target]!;
      st.hits++;
      st.mask |= b.color;
    } else if (b.reason === 'captured') {
      captured++;
    }
  }
  level.receivers.forEach((r, i) => {
    const st = receivers[i]!;
    st.lit = st.hits >= receiverNeeds(r) && st.mask === r.color;
  });
  const won = receivers.length > 0 && receivers.every((r) => r.lit);
  return { beams, receivers, won, captured };
}

/** Does this set of placements win the level? */
export function wins(level: Level, placed: readonly Mass[]): boolean {
  return traceLevel(level, placed, { record: false }).won;
}

// ---------------------------------------------------------------------------------------------
// Inventory, weight and placement rules

export function weightOf(masses: readonly Mass[]): number {
  return masses.reduce((sum, m) => sum + MASS_SPECS[m.kind].weight, 0);
}

export function countKinds(masses: readonly Mass[]): Record<MassKind, number> {
  const c = { moon: 0, star: 0, giant: 0, hole: 0, void: 0 };
  for (const m of masses) c[m.kind]++;
  return c;
}

/** How many of each kind are still in the tray. */
export function remaining(inv: Inventory, placed: readonly Mass[]): Record<MassKind, number> {
  const used = countKinds(placed);
  const out = { moon: 0, star: 0, giant: 0, hole: 0, void: 0 };
  for (const k of MASS_KINDS) out[k] = Math.max(0, (inv[k] ?? 0) - used[k]);
  return out;
}

export function fitsInventory(inv: Inventory, placed: readonly Mass[]): boolean {
  const used = countKinds(placed);
  return MASS_KINDS.every((k) => used[k] <= (inv[k] ?? 0));
}

function segDist(px: number, py: number, w: Wall): number {
  const ex = w.x2 - w.x1;
  const ey = w.y2 - w.y1;
  const l2 = ex * ex + ey * ey;
  let t = l2 > 0 ? ((px - w.x1) * ex + (py - w.y1) * ey) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (w.x1 + t * ex), py - (w.y1 + t * ey));
}

/**
 * Can a mass of this kind sit at (x, y)? It must be on the plate and clear of every solid
 * element (beacons, telescopes, walls, rocks and other masses). Clouds and nebulae may overlap.
 * `ignore` is the index in `placed` of the mass being moved.
 */
export function canPlace(
  level: Level,
  placed: readonly Mass[],
  kind: MassKind,
  x: number,
  y: number,
  ignore = -1,
): boolean {
  const r = MASS_SPECS[kind].radius;
  if (x < r || y < r || x > WORLD_W - r || y > WORLD_H - r) return false;
  const clear = (cx: number, cy: number, cr: number) =>
    Math.hypot(x - cx, y - cy) >= r + cr + PLACE_GAP;
  for (const s of level.sources) if (!clear(s.x, s.y, SOURCE_RADIUS)) return false;
  for (const t of level.receivers) if (!clear(t.x, t.y, RECEIVER_RADIUS)) return false;
  for (const k of level.rocks) if (!clear(k.x, k.y, k.r)) return false;
  for (const w of level.walls) if (segDist(x, y, w) < r + WALL_HALF + PLACE_GAP) return false;
  for (const m of level.fixed) if (!clear(m.x, m.y, MASS_SPECS[m.kind].radius)) return false;
  for (let i = 0; i < placed.length; i++) {
    if (i === ignore) continue;
    const m = placed[i]!;
    if (!clear(m.x, m.y, MASS_SPECS[m.kind].radius)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------------------------
// Validation of level data (bundled JSON and imported share codes)

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const inX = (v: unknown) => num(v) && v >= 0 && v <= WORLD_W;
const inY = (v: unknown) => num(v) && v >= 0 && v <= WORLD_H;
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isKind = (v: unknown): v is MassKind => MASS_KINDS.includes(v as MassKind);

export type Validation = { ok: true; level: Level } | { ok: false; errors: string[] };

/** Validate untrusted data and return a normalised level. */
export function validateLevel(data: unknown): Validation {
  const errors: string[] = [];
  if (!isObj(data)) return { ok: false, errors: ['level is not an object'] };
  const list = (key: string): unknown[] => {
    const v = data[key];
    if (v === undefined) return [];
    if (!Array.isArray(v)) {
      errors.push(`${key} must be an array`);
      return [];
    }
    return v;
  };
  if (typeof data.id !== 'string' || data.id.length === 0) errors.push('id must be a string');
  if (typeof data.name !== 'string' || data.name.length === 0 || data.name.length > 60) {
    errors.push('name must be 1-60 characters');
  }
  if (data.hint !== undefined && typeof data.hint !== 'string') errors.push('hint must be text');

  const sources: Source[] = [];
  list('sources').forEach((s, i) => {
    if (!isObj(s) || !inX(s.x) || !inY(s.y) || !num(s.angle) || !isValidColor(s.color)) {
      errors.push(`source ${i} is invalid`);
      return;
    }
    const beams = s.beams ?? 1;
    const spread = s.spread ?? 0;
    if (!num(beams) || !Number.isInteger(beams) || beams < 1 || beams > 3) {
      errors.push(`source ${i} beams must be 1-3`);
      return;
    }
    if (!num(spread) || spread < 0 || spread > 60) {
      errors.push(`source ${i} spread must be 0-60 degrees`);
      return;
    }
    const src: Source = {
      x: s.x as number,
      y: s.y as number,
      angle: s.angle as number,
      color: s.color,
    };
    if (beams > 1) {
      src.beams = beams;
      src.spread = spread;
    }
    sources.push(src);
  });

  const receivers: Receiver[] = [];
  list('receivers').forEach((r, i) => {
    if (!isObj(r) || !inX(r.x) || !inY(r.y) || !isValidColor(r.color)) {
      errors.push(`receiver ${i} is invalid`);
      return;
    }
    const count = r.count ?? 1;
    if (!num(count) || !Number.isInteger(count) || count < 1 || count > 3) {
      errors.push(`receiver ${i} count must be 1-3`);
      return;
    }
    const rec: Receiver = { x: r.x as number, y: r.y as number, color: r.color };
    if (count > 1) rec.count = count;
    receivers.push(rec);
  });

  const walls: Wall[] = [];
  list('walls').forEach((w, i) => {
    if (!isObj(w) || !inX(w.x1) || !inY(w.y1) || !inX(w.x2) || !inY(w.y2)) {
      errors.push(`wall ${i} is invalid`);
      return;
    }
    walls.push({ x1: w.x1 as number, y1: w.y1 as number, x2: w.x2 as number, y2: w.y2 as number });
  });

  const discs = (key: string, rMin: number, rMax: number): Disc[] => {
    const out: Disc[] = [];
    list(key).forEach((d, i) => {
      if (!isObj(d) || !inX(d.x) || !inY(d.y) || !num(d.r) || d.r < rMin || d.r > rMax) {
        errors.push(`${key} ${i} is invalid`);
        return;
      }
      out.push({ x: d.x as number, y: d.y as number, r: d.r });
    });
    return out;
  };
  const rocks = discs('rocks', 6, 120);
  const dust = discs('dust', 10, 200);

  const nebulae: Nebula[] = [];
  list('nebulae').forEach((n, i) => {
    if (
      !isObj(n) ||
      !inX(n.x) ||
      !inY(n.y) ||
      !num(n.r) ||
      n.r < 20 ||
      n.r > 260 ||
      !isValidColor(n.color)
    ) {
      errors.push(`nebula ${i} is invalid`);
      return;
    }
    nebulae.push({ x: n.x as number, y: n.y as number, r: n.r, color: n.color });
  });

  const masses = (key: string): Mass[] => {
    const out: Mass[] = [];
    list(key).forEach((m, i) => {
      if (!isObj(m) || !isKind(m.kind) || !inX(m.x) || !inY(m.y)) {
        errors.push(`${key} ${i} is invalid`);
        return;
      }
      out.push({ kind: m.kind, x: m.x as number, y: m.y as number });
    });
    return out;
  };
  const fixed = masses('fixed');
  const solution = masses('solution');

  const inventory: Inventory = {};
  if (data.inventory !== undefined) {
    if (!isObj(data.inventory)) errors.push('inventory must be an object');
    else {
      for (const [k, v] of Object.entries(data.inventory)) {
        if (!isKind(k)) errors.push(`unknown inventory kind ${k}`);
        else if (!num(v) || !Number.isInteger(v) || v < 0 || v > 9) {
          errors.push(`inventory ${k} must be 0-9`);
        } else if (v > 0) inventory[k] = v;
      }
    }
  }
  const par = data.par ?? weightOf(solution);
  if (!num(par) || par < 0) errors.push('par must be a non-negative number');

  if (sources.length === 0) errors.push('a level needs at least one beacon');
  if (receivers.length === 0) errors.push('a level needs at least one telescope');
  if (sources.length > 8) errors.push('at most 8 beacons');
  if (receivers.length > 8) errors.push('at most 8 telescopes');
  if (walls.length + rocks.length + dust.length + nebulae.length > 40)
    errors.push('too many elements');
  if (fixed.length > 8) errors.push('at most 8 fixed masses');
  if (solution.length > 0 && !fitsInventory(inventory, solution)) {
    errors.push('the solution uses masses that are not in the inventory');
  }

  if (errors.length > 0) return { ok: false, errors };
  const level: Level = {
    id: data.id as string,
    name: data.name as string,
    sources,
    receivers,
    walls,
    rocks,
    dust,
    nebulae,
    fixed,
    inventory,
    par: par as number,
    solution,
  };
  if (typeof data.hint === 'string' && data.hint.length > 0) level.hint = data.hint;
  return { ok: true, level };
}

/** An empty level shell (used by the editor). */
export function emptyLevel(): Level {
  return {
    id: 'custom',
    name: 'Untitled plate',
    sources: [],
    receivers: [],
    walls: [],
    rocks: [],
    dust: [],
    nebulae: [],
    fixed: [],
    inventory: {},
    par: 0,
    solution: [],
  };
}
